import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/Layout";
import { FormCheck, MissingNote, nativeValid, useFormCheck } from "@/components/forms";
import { DataTable, type DataTableColumn } from "@/components/table";
import { Button, ErrorNote, Field, Hint, KeyFigures, SidePanel, inputClass, useCurrentUser, useIsAdmin } from "@/components/ui";
import { api, errorMessage } from "@/lib/api";
import { date } from "@/lib/format";
import { keys, useUsers } from "@/lib/queries";
import type { CreatableRole, UserRow } from "@/lib/types";

/*
 * UTENTI INTERNI: chi entra nel gestionale (non i clienti: quelli hanno il loro
 * accesso dalla scheda del cliente).
 *
 * Solo ADMIN, perché le rotte sono sue. Si CREANO operatori, contabili e utenti
 * di sola lettura: **un amministratore non si crea da qui**, si crea a mano sulla
 * VPS — il ruolo ADMIN non compare fra le scelte perché la rotta non lo accetta.
 * Gli utenti si disattivano e non si cancellano (l'audit tiene il loro nome), e
 * la password non si sceglie e non si rilegge: si genera, si mostra una volta.
 */

const ROLE_LABEL: Record<UserRow["role"], string> = {
  ADMIN: "Amministratore",
  OPERATORE: "Operatore",
  CONTABILE: "Contabile",
  VIEWER: "Sola lettura",
  CLIENT: "Cliente",
};

const ROLE_HINT: Record<CreatableRole, string> = {
  OPERATORE: "Opera le fasi e le operazioni, gestisce clienti, conti e anagrafiche. Non tocca gli utenti.",
  CONTABILE: "Legge tutto e scrive nel registro contabile (movimenti e payout). Non opera sul mercato.",
  VIEWER: "Sola lettura: vede, non scrive.",
};

const CREATABLE: CreatableRole[] = ["OPERATORE", "CONTABILE", "VIEWER"];

export function Users() {
  // Il controllo sta fuori: senza, un non-ADMIN farebbe partire la lettura (un 403) prima di vedere il messaggio.
  if (!useIsAdmin()) {
    return (
      <>
        <PageHeader title="Utenti interni" />
        <div className="px-6 py-6 text-sm text-muted">Gli utenti li gestisce un amministratore.</div>
      </>
    );
  }
  return <UsersAdmin />;
}

function UsersAdmin() {
  const me = useCurrentUser();
  const users = useUsers();
  const qc = useQueryClient();
  const [panel, setPanel] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [shown, setShown] = useState<{ email: string; password: string } | null>(null);

  // `GET /users` restituisce anche gli accessi dei clienti: qui si vedono solo quelli interni.
  const rows = (users.data ?? []).filter((u) => u.role !== "CLIENT");
  const active = rows.filter((u) => u.isActive);

  const act = async (id: string, fn: () => Promise<void>) => {
    setBusyId(id);
    setError(null);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: keys.users });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  const columns: DataTableColumn<UserRow>[] = [
    {
      key: "email",
      header: "Utente",
      sortBy: (u) => u.email,
      cell: (u) => (
        <span>
          <span className="machine-id">{u.email}</span>
          {u.fullName && <span className="ml-2 text-muted">{u.fullName}</span>}
          {u.id === me.id && <span className="ml-2 text-xs text-faint">(tu)</span>}
        </span>
      ),
    },
    { key: "role", header: "Ruolo", sortBy: (u) => ROLE_LABEL[u.role], cell: (u) => ROLE_LABEL[u.role] },
    {
      key: "state",
      header: "Stato",
      sortBy: (u) => (u.isActive ? 0 : 1),
      cell: (u) => (
        <span>
          <span className={u.isActive ? "text-gain" : "text-loss"}>{u.isActive ? "Attivo" : "Disattivato"}</span>
          {u.mustChangePassword && <span className="ml-2 text-xs text-muted">password da cambiare</span>}
        </span>
      ),
    },
    { key: "created", header: "Creato", sortBy: (u) => u.createdAt, cell: (u) => date(u.createdAt) },
    {
      key: "actions",
      header: <span className="sr-only">Azioni</span>,
      align: "right",
      cell: (u) => (
        <span className="inline-flex flex-wrap items-center justify-end gap-2">
          {/* Non ci si disattiva da soli: il backend lo rifiuta (SELF_DISABLE), qui non si offre. */}
          {u.id !== me.id && (
            <Button size="sm" disabled={busyId === u.id} onClick={() => void act(u.id, async () => void (await api.users.setActive(u.id, !u.isActive)))}>
              {u.isActive ? "Disattiva" : "Riattiva"}
            </Button>
          )}
          <Button
            size="sm"
            disabled={busyId === u.id}
            onClick={() =>
              void act(u.id, async () => {
                const nuova = await api.users.resetPassword(u.id);
                setShown({ email: nuova.email, password: nuova.password });
              })
            }
          >
            Rigenera password
          </Button>
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Utenti interni"
        subtitle="Chi entra nel gestionale: operatori, contabili e sola lettura. I clienti hanno il loro accesso dalla loro scheda"
        actions={
          <Button variant="primary" onClick={() => setPanel(true)}>
            Nuovo utente
          </Button>
        }
      />

      <div className="space-y-4 px-6 py-4">
        {users.isLoading ? (
          <p className="text-sm text-muted">Caricamento…</p>
        ) : users.error ? (
          <ErrorNote>{errorMessage(users.error)}</ErrorNote>
        ) : (
          <>
            <KeyFigures
              items={[
                { label: "Utenti attivi", value: active.length, hint: "Quelli che possono entrare adesso." },
                {
                  label: "Amministratori attivi",
                  value: active.filter((u) => u.role === "ADMIN").length,
                  hint: "Un amministratore non si crea da qui: si crea a mano sulla VPS, un gesto raro e tracciato fuori dall'interfaccia. L'ultimo attivo non si può disattivare.",
                },
                { label: "Disattivati", value: rows.length - active.length, hint: "Non entrano più, ma restano: l'audit tiene il loro nome accanto a ogni azione." },
              ]}
            />

            {shown && (
              <div role="status" className="space-y-2 rounded border border-line bg-surface px-3 py-2">
                <p className="text-ink">
                  <strong>Si vede solo adesso</strong>: nel database c'è soltanto l'hash, e una password non si rilegge — si sostituisce.
                  Al primo accesso l'utente dovrà cambiarla.
                </p>
                <dl className="space-y-1 text-sm">
                  <div className="flex justify-between gap-3">
                    <dt className="text-muted">Email</dt>
                    <dd className="font-mono text-ink">{shown.email}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-muted">Password</dt>
                    <dd className="select-all font-mono text-ink">{shown.password}</dd>
                  </div>
                </dl>
                <div className="flex justify-end">
                  <Button size="sm" variant="primary" onClick={() => setShown(null)}>
                    Ho copiato la password
                  </Button>
                </div>
              </div>
            )}

            {error && <ErrorNote>{error}</ErrorNote>}

            <div className="card">
              <DataTable
                columns={columns}
                rows={rows}
                rowKey={(u) => u.id}
                initialSort={{ key: "state", dir: "asc" }}
                empty={{
                  title: "Nessun utente interno",
                  hint: "Aggiungi un operatore: riceve una password generata, che si vede una volta sola e che deve cambiare al primo accesso.",
                  action: (
                    <Button variant="primary" onClick={() => setPanel(true)}>
                      Nuovo utente
                    </Button>
                  ),
                }}
              />
            </div>
          </>
        )}
      </div>

      {panel && (
        <NewUserPanel
          onClose={() => setPanel(false)}
          onCreated={(email, password) => {
            setShown({ email, password });
            setPanel(false);
          }}
        />
      )}
    </>
  );
}

function NewUserPanel({ onClose, onCreated }: { onClose: () => void; onCreated: (email: string, password: string) => void }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  // Nessun ruolo preselezionato: chi crea l'accesso sceglie che cosa può fare.
  const [role, setRole] = useState<CreatableRole | "">("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const check = useFormCheck([
    ["Email d'accesso", !!email.trim() && nativeValid("email", email.trim())],
    ["Ruolo", !!role],
  ]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!check.attempt() || !role) return;
    setBusy(true);
    setError(null);
    try {
      const creato = await api.users.create({ email: email.trim(), fullName: fullName.trim() || null, role });
      await qc.invalidateQueries({ queryKey: keys.users });
      onCreated(creato.email, creato.password);
    } catch (err) {
      setError(errorMessage(err));
      check.fromError(err, { email: "Email d'accesso", EMAIL_TAKEN: "Email d'accesso", fullName: "Nome", role: "Ruolo" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <SidePanel
      open
      onClose={onClose}
      title="Nuovo utente"
      subtitle="La password la genera il gestionale e si vede una volta sola"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button variant="primary" type="submit" form="user-form" disabled={busy}>
            {busy ? "Creo…" : "Crea utente"}
          </Button>
          <MissingNote check={check} end />
        </>
      }
    >
      <FormCheck check={check}>
      <form id="user-form" noValidate onSubmit={submit} className="space-y-4">
        <Field label="Email d'accesso" hint="Unica in tutto il gestionale: è con lei che l'utente entra.">
          {(id) => <input id={id} type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} />}
        </Field>
        <Field label="Nome" hint="Facoltativo.">
          {(id) => <input id={id} value={fullName} onChange={(e) => setFullName(e.target.value)} className={inputClass} />}
        </Field>
        <Field label="Ruolo">
          {(id) => (
            <div id={id} role="radiogroup" className="space-y-2">
              {CREATABLE.map((r) => (
                <button
                  key={r}
                  type="button"
                  role="radio"
                  aria-checked={role === r}
                  onClick={() => setRole(r)}
                  className={`block w-full rounded border px-3 py-2 text-left text-sm ${role === r ? "border-accent bg-accent/10" : "border-line hover:border-accent/50"}`}
                >
                  <span className="font-medium text-ink">{ROLE_LABEL[r]}</span>
                  <span className="mt-0.5 block text-xs text-muted">{ROLE_HINT[r]}</span>
                </button>
              ))}
            </div>
          )}
        </Field>
        <p className="flex items-center text-xs text-faint">
          Un amministratore non si crea da qui.
          <Hint>Un amministratore si crea solo a mano sulla VPS: resta un gesto raro e tracciato fuori dall'interfaccia normale.</Hint>
        </p>
        <ErrorNote>{error}</ErrorNote>
      </form>
      </FormCheck>
    </SidePanel>
  );
}
